/**
 * useAuth Hook
 * Authentication utilities
 */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import {
  setCredentials,
  updateUser,
  updateToken,
  logout as logoutAction,
  setLoading,
} from '@/store/slices/authSlice';
import apiService, { setLogoutCallback, setTokenUpdateCallback } from '@/services/api.service';
import { retryAfterSeconds } from '@/hooks/useCooldown';
import {
  checkDomain as checkDomainApi,
  requestToJoin as requestToJoinApi,
  startSignup as startSignupApi,
  verifySignupOtp as verifySignupOtpApi,
  completeSignup as completeSignupApi,
  googlePreview as googlePreviewApi,
  googleComplete as googleCompleteApi,
  createOrganization as createOrganizationApi,
  getOrganizationRequestStatus as getOrganizationRequestStatusApi,
  type AuthResult,
  type CompleteSignupPayload,
  type CreateOrganizationPayload,
  type GoogleCompletePayload,
  type StartSignupOptions,
} from '@/services/auth.api';
import {
  acceptInviteNewIdentity,
  acceptInviteExisting,
  type AcceptInviteNewIdentityPayload,
} from '@/services/invite.api';

/**
 * Guards every place a `?redirect=` query param (set by ProtectedRoute when
 * bouncing an unauthenticated visitor to /login, or by the invite-accept
 * flow) reaches `navigate()` — see computeDestination below and
 * pages/admin/Login.tsx's post-login effect, the two call sites. React
 * Router's `navigate()` won't itself hop to a different origin for a string
 * like "https://evil.com" (it's resolved as a relative path segment, not a
 * browser-level redirect), but nothing enforces that today except that
 * incidental behavior — this makes the requirement explicit and rejects
 * protocol-relative (`//evil.com`) and non-relative values outright, rather
 * than relying on a downstream library's parsing quirks to stay safe.
 * Same-origin, path-only values (starting with a single `/`, never `//`)
 * are the only thing ever passed through.
 */
export function isSafeInternalPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, tenant, token, isAuthenticated, loading } = useSelector(
    (state: RootState) => state.auth
  );

  // Set up logout callback for API service to use when session expires
  React.useEffect(() => {
    const handleLogout = () => {
      dispatch(logoutAction());
      navigate('/login', { replace: true });
    };
    const unregisterLogout = setLogoutCallback(handleLogout);

    // When the API service silently refreshes the access token, mirror it into
    // Redux so `state.auth.token` never lags behind localStorage.
    const unregisterTokenUpdate = setTokenUpdateCallback((token: string) => {
      dispatch(updateToken({ token }));
    });

    // Remove only THIS instance's callbacks — other mounted useAuth() users
    // (the layout, at minimum) keep theirs. Blanking the global slot here used
    // to leave a forced logout (expired/revoked session) with no redirect.
    return () => {
      unregisterLogout();
      unregisterTokenUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate]);

  // Note: Auth state is already initialized from localStorage in authSlice
  // This effect is only for edge cases where state might be out of sync
  React.useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    const storedToken = localStorage.getItem('admin_token');
    const storedUser = localStorage.getItem('admin_user');

    // Only sync if we have stored data but Redux state doesn't match
    if (storedToken && storedUser) {
      if (!token || !user) {
        try {
          const userObj = JSON.parse(storedUser);
          dispatch(
            setCredentials({
              user: userObj,
              token: storedToken,
            })
          );
        } catch (error) {
          // Invalid stored data, clear it
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('refresh_token');
        }
      }
    } else if (!storedToken && !storedUser && isAuthenticated) {
      // If localStorage is cleared but Redux thinks we're authenticated, log out
      dispatch(logoutAction());
    }
  }, [dispatch, token, user, isAuthenticated]);

  /** Reads an axios/fetch error thrown by any auth.api.ts call into a
   *  user-facing message, matching login()'s original error-extraction logic
   *  so every auth method (login, signup steps, google, create-org)
   *  surfaces errors the same way. */
  const extractErrorMessage = (error: any, fallback: string): string => {
    if (error.response?.data) {
      const responseData = error.response.data;
      return responseData.error?.message || responseData.message || fallback;
    }
    if (error.message) return error.message;
    return fallback;
  };

  /** The backend's ErrorCode enum value (e.g. 'DOMAIN_ALREADY_CLAIMED'), when
   *  present — lets a caller branch on the failure REASON instead of
   *  string-matching extractErrorMessage's human-readable text. */
  const extractErrorCode = (error: any): string | undefined => {
    return error.response?.data?.error?.code;
  };

  /** The destination applyAuthResult navigates to once a session is issued. */
  const computeDestination = (result: AuthResult, redirectTo?: string): string => {
    if (isSafeInternalPath(redirectTo)) return redirectTo;
    if (!result.tenant) return '/onboarding';
    const role = result.user.role;
    return role === 'tenant_owner' || role === 'superadmin' || role === 'tenant_admin'
      ? '/admin/dashboard'
      : '/dashboard';
  };

  /**
   * Shared by every method that ends a request holding a fresh session
   * (login, complete-signup, Google sign-in, create-organization,
   * invite-accept-new-identity) — stores credentials and redirects.
   *
   * `result.tenant` absent means a PENDING identity (signed up/invited but
   * hasn't created or joined a tenant yet, or hasn't verified) — these always
   * go to /onboarding, never a dashboard route that assumes tenant context.
   * This is also the fix for a real bug: the original login() redirected by
   * role alone, so a pending tenant_owner (no tenant) landed on
   * /admin/dashboard and broke on the first tenant-scoped fetch.
   */
  const applyAuthResult = (result: AuthResult, redirectTo?: string) => {
    if (result.tokens?.refreshToken) {
      localStorage.setItem('refresh_token', result.tokens.refreshToken);
    }
    dispatch(
      setCredentials({
        user: result.user as any,
        tenant: (result.tenant as any) || undefined,
        token: result.tokens.accessToken,
      })
    );
    dispatch(setLoading(false));

    // No setTimeout here (the original login() had one "to ensure Redux state
    // is updated") — dispatch() commits to the store synchronously, so by the
    // time navigate() below runs, every useSelector-connected component will
    // already read the new state on its next render regardless of timing.
    // The delay was pure added latency on every single auth transition.
    navigate(computeDestination(result, redirectTo), { replace: true });
  };

  /** `redirectTo` overrides the default post-login destination — used by the
   *  invite-accept flow to send someone through /login and land back on
   *  their invite instead of the dashboard. */
  const login = async (email: string, password: string, redirectTo?: string) => {
    dispatch(setLoading(true));
    try {
      if (!email || !password) {
        dispatch(setLoading(false));
        return { success: false, error: 'Email and password are required' };
      }

      const data = await apiService.post('/auth/login', { email: email.trim(), password });

      if (data.success && data.data) {
        if (!data.data.user || !data.data.tokens?.accessToken) {
          dispatch(setLoading(false));
          return { success: false, error: 'Invalid response from server' };
        }
        applyAuthResult(data.data as AuthResult, redirectTo);
        return { success: true };
      } else {
        dispatch(setLoading(false));
        const errorMessage = data.error?.message || data.message || 'Login failed';
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      dispatch(setLoading(false));
      return {
        success: false,
        error: extractErrorMessage(error, 'Network error. Please check if the backend server is running.'),
        // Set only on 429 (too many attempts) — Login disables the button for this long.
        retryAfter: retryAfterSeconds(error),
      };
    }
  };

  /** Pre-flight check for the Slack-style "workspace already exists" UI
   *  branch — called before startSignup, no credential change. */
  const checkDomain = async (email: string) => {
    try {
      const result = await checkDomainApi(email);
      return { success: true as const, result };
    } catch (error: any) {
      return { success: false as const, error: extractErrorMessage(error, 'Could not check this email') };
    }
  };

  /** "Request to join" — a separate, opt-in action; never a prerequisite
   *  for or blocker of the normal signup flow. `tenantId` picks which
   *  workspace when checkDomain returned more than one for this domain. */
  const requestToJoin = async (email: string, tenantId: string, name?: string) => {
    try {
      const result = await requestToJoinApi(email, tenantId, name);
      return { success: true as const, message: result.message };
    } catch (error: any) {
      return { success: false as const, error: extractErrorMessage(error, 'Could not send the request') };
    }
  };

  /** Signup step 1 — email only, no credential/auth-state change. The
   *  backend is deliberately explicit (409) if the email already has an
   *  account, so a failure here usually means "nudge them to /login". */
  const startSignup = async (email: string, options?: StartSignupOptions) => {
    try {
      const result = await startSignupApi(email, options);
      return { success: true as const, message: result.message };
    } catch (error: any) {
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Could not send the verification code'),
        code: extractErrorCode(error),
      };
    }
  };

  /** Signup step 2 — verify the OTP. No credential change; hands back the
   *  signup ticket the wizard carries (in component state, never the URL)
   *  into step 3. */
  const verifySignupOtp = async (email: string, otp: string) => {
    try {
      const result = await verifySignupOtpApi(email, otp);
      return { success: true, signupTicket: result.signupTicket };
    } catch (error: any) {
      return { success: false, error: extractErrorMessage(error, 'Invalid or expired code') };
    }
  };

  /** Signup step 3 — redeem the ticket, name+password, actually creates the
   *  (already-verified) identity. Always tenant-less at this point, so
   *  applyAuthResult's default redirect correctly sends them to /onboarding. */
  const completeSignup = async (payload: CompleteSignupPayload) => {
    dispatch(setLoading(true));
    try {
      const result = await completeSignupApi(payload);
      applyAuthResult(result);
      return { success: true };
    } catch (error: any) {
      dispatch(setLoading(false));
      return { success: false, error: extractErrorMessage(error, 'Could not create your account') };
    }
  };

  /**
   * Step 1 of Google sign-in. A known identity (googleId match, or
   * auto-link by verified email) resolves straight through applyAuthResult —
   * nothing new is being asserted about that person, so there's nothing to
   * confirm, same as password login never re-asking your name.
   *
   * A brand-new identity does NOT log in here — no account exists yet. It
   * returns `{ isNewIdentity: true, googleTicket, preview }` so the caller
   * can render a confirm-your-details screen with the previewed data, and
   * only call completeGoogleSignIn() once the user explicitly confirms it.
   */
  const googlePreview = async (idToken: string) => {
    dispatch(setLoading(true));
    try {
      const result = await googlePreviewApi(idToken);
      if (result.isNewIdentity) {
        dispatch(setLoading(false));
        return { success: true as const, isNewIdentity: true as const, googleTicket: result.googleTicket, preview: result.preview };
      }
      applyAuthResult(result);
      return { success: true as const, isNewIdentity: false as const };
    } catch (error: any) {
      dispatch(setLoading(false));
      return { success: false as const, error: extractErrorMessage(error, 'Google sign-in failed') };
    }
  };

  /** Step 2 — only called once the user has reviewed and confirmed (or
   *  edited) the name on the confirm-your-details screen. THIS is the moment
   *  the account is actually created — nothing was persisted in googlePreview. */
  const completeGoogleSignIn = async (payload: GoogleCompletePayload) => {
    dispatch(setLoading(true));
    try {
      const result = await googleCompleteApi(payload);
      applyAuthResult(result);
      return { success: true as const };
    } catch (error: any) {
      dispatch(setLoading(false));
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Could not create your account'),
        code: extractErrorCode(error),
      };
    }
  };

  /** Requires an existing (identity-only or full) session — the bearer token
   *  is already attached by api.service's interceptor. Files a REQUEST, not
   *  a tenant — see auth.service.ts#requestTenant on the backend. No
   *  tokens/tenant come back, so this does NOT go through applyAuthResult;
   *  the caller (Onboarding.tsx) navigates to the pending screen itself. */
  const createOrganization = async (payload: CreateOrganizationPayload) => {
    dispatch(setLoading(true));
    try {
      const result = await createOrganizationApi(payload);
      dispatch(setLoading(false));
      return { success: true as const, requestId: result.requestId };
    } catch (error: any) {
      dispatch(setLoading(false));
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Could not create your organization'),
      };
    }
  };

  /** Checks whether the current tenant-less identity already has an
   *  organization request in flight — lets Onboarding.tsx/OnboardingPending.tsx
   *  redirect a returning user to the right screen instead of re-showing a
   *  form they already submitted. */
  const getOrganizationRequestStatus = async () => {
    try {
      const request = await getOrganizationRequestStatusApi();
      return { success: true as const, request };
    } catch (error: any) {
      return {
        success: false as const,
        error: extractErrorMessage(error, 'Could not check your request status'),
      };
    }
  };

  /** Invite accept — brand-new identity. Always resolves to the invite's
   *  tenant, so the default redirect lands straight on the dashboard, no
   *  onboarding step (they're joining an existing org, not creating one). */
  const acceptInvite = async (token: string, payload: AcceptInviteNewIdentityPayload) => {
    dispatch(setLoading(true));
    try {
      const result = await acceptInviteNewIdentity(token, payload);
      applyAuthResult(result);
      return { success: true };
    } catch (error: any) {
      dispatch(setLoading(false));
      return {
        success: false,
        error: extractErrorMessage(error, 'Could not accept this invitation'),
      };
    }
  };

  /**
   * Invite accept — caller is already logged in as the invited email, as a
   * tenant-less session (see module comment: this is the normal case
   * createInvite() now allows through). Never touches their PASSWORD, but
   * DOES adopt a fresh token/tenant/permissions triple the backend mints —
   * their existing session was issued before they had a tenant, and
   * ProtectedRoute reads Redux's `tenant`, not the DB, so without this the
   * "you've joined" confirmation would succeed here and then immediately
   * bounce back to /onboarding the moment they click through to the
   * dashboard. Deliberately does NOT navigate — InviteAccept.tsx shows its
   * own confirmation screen first; ProtectedRoute just needs the adopted
   * state to be correct by the time its own "Go to dashboard" button fires.
   */
  const acceptInviteAsExistingUser = async (token: string) => {
    try {
      const result = await acceptInviteExisting(token);
      if (result.tokens?.refreshToken) {
        localStorage.setItem('refresh_token', result.tokens.refreshToken);
      }
      dispatch(
        setCredentials({
          user: result.user as any,
          tenant: (result.tenant as any) || undefined,
          token: result.tokens.accessToken,
        })
      );
      return { success: true, tenant: result.tenant };
    } catch (error: any) {
      return {
        success: false,
        error: extractErrorMessage(error, 'Could not accept this invitation'),
      };
    }
  };

  const logout = () => {
    // Revoke the server-side session first (read the token before it's
    // cleared). Fire-and-forget — logout never waits on, or fails with, the network.
    const refreshTokenValue =
      typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
    if (refreshTokenValue) {
      apiService.post('/auth/logout', { refreshToken: refreshTokenValue }).catch(() => {});
    }

    // Clear all auth data
    dispatch(logoutAction());

    // Clear any additional tokens (logout action already clears these, but ensure cleanup)
    if (typeof window !== 'undefined') {
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    }

    // Navigate to login
    navigate('/login', { replace: true });
  };

  const hasRole = (requiredRoles: string[]): boolean => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  /**
   * Refresh access token using refresh token
   */
  const refreshToken = async (): Promise<boolean> => {
    try {
      // Only run in browser environment
      if (typeof window === 'undefined') return false;

      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        console.warn('No refresh token available');
        return false;
      }

      // Use apiService instead of fetch to ensure proper URL handling
      // This prevents URL resolution issues where localhost:5000 might become dooktravels.localhost:5000
      const data = await apiService.post('/auth/refresh', {
        refreshToken: refreshTokenValue,
      });

      if (data.success && data.data?.tokens) {
        // Update tokens
        localStorage.setItem('admin_token', data.data.tokens.accessToken);
        if (data.data.tokens.refreshToken) {
          localStorage.setItem('refresh_token', data.data.tokens.refreshToken);
        }

        // Update Redux state - just update the token, keep user data
        if (user) {
          dispatch(updateToken({ token: data.data.tokens.accessToken }));
        } else {
          // If no user in state, we need to set credentials
          // This shouldn't happen, but handle it gracefully
          console.warn('No user in state during token refresh');
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  };

  /**
   * Refresh current user data from backend (including fresh module access)
   */
  const refreshUser = async (): Promise<boolean> => {
    if (!token || !isAuthenticated) {
      return false;
    }

    try {
      const response = await apiService.getCurrentUser();
      if (response.success && response.data?.user) {
        dispatch(
          updateUser({
            user: response.data.user,
            tenant: response.data.tenant || undefined,
          })
        );
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Failed to refresh user data:', error);
      // If it's a 401, the token might be expired - let the API interceptor handle it
      if (error.response?.status === 401) {
        // The API service interceptor will handle logout
        return false;
      }
      return false;
    }
  };

  return {
    user,
    tenant,
    token,
    isAuthenticated,
    loading,
    login,
    checkDomain,
    requestToJoin,
    startSignup,
    verifySignupOtp,
    completeSignup,
    googlePreview,
    completeGoogleSignIn,
    createOrganization,
    getOrganizationRequestStatus,
    acceptInvite,
    acceptInviteAsExistingUser,
    logout,
    hasRole,
    refreshUser,
    refreshToken,
  };
};
